import { useState } from 'react'
import { Bell, Zap, Clock, ArrowRight, ChevronDown, AlertCircle, CheckCircle2, Settings, Key, Hash } from 'lucide-react'
import AnchorLink from './AnchorLink'

const SubSectionHeading = ({ children, id }) => (
  <div className="mb-5" id={id}>
    <div className="group flex items-center gap-2">
      <h3 className="wiki-heading text-lg font-semibold text-light-text-primary dark:text-dark-text-primary">
        {children}
      </h3>
      {id && <AnchorLink id={id} />}
    </div>
    <div className="mt-2 h-px bg-light-border-primary dark:bg-dark-border-primary" />
  </div>
)

const flowSteps = [
  {
    icon: Bell,
    label: 'Event',
    desc: 'Action occurs in the system',
    color: 'text-primary-500',
    bg: 'bg-primary-50 dark:bg-primary-900/20',
  },
  {
    icon: Zap,
    label: 'Service',
    desc: 'DiscordNotificationService picks it up',
    color: 'text-yellow-600 dark:text-yellow-400',
    bg: 'bg-yellow-50 dark:bg-yellow-900/20',
  },
  {
    icon: Key,
    label: 'Credentials',
    desc: 'Bot token + channel ID from account-service',
    color: 'text-blue-600 dark:text-blue-400',
    bg: 'bg-blue-50 dark:bg-blue-900/20',
  },
  {
    icon: Hash,
    label: 'Discord',
    desc: 'Embed message sent to the channel',
    color: 'text-indigo-600 dark:text-indigo-400',
    bg: 'bg-indigo-50 dark:bg-indigo-900/20',
  },
]

const realtimeEvents = [
  {
    type: 'talent_status_changed',
    label: 'Stage → Video',
    desc: 'Fires when a talent reaches the "Video" stage of the pipeline — on creation, or via a status/stage change that lands them there.',
    embed: ['Talent name', 'Previous stage → New stage', 'Responsible manager'],
  },
  {
    type: 'talent_refused',
    label: 'Talent refused',
    desc: 'Fires when a talent moves into a refusal status (the DISCORD_REFUSAL_STATUSES list in shared/talentStatuses.json, currently "Refused") — from the card, the list, or via API token / MCP.',
    embed: ['Talent name with a link to the card', 'Stage the talent refused from', 'Previous status → refusal status', 'Who changed it', 'Reason (if given)'],
  },
  {
    type: 'talent_post_published',
    label: 'Post published',
    desc: 'Fires when a talent post is published to the public catalog.',
    embed: ['Talent name', 'Post title', 'Link to the post'],
  },
]

const scheduledEvents = [
  {
    type: 'talent_birthday_tomorrow',
    label: 'Birthday tomorrow',
    time: '10:00 Kyiv',
    desc: 'A single daily digest listing every talent whose birthday is the next calendar day, grouped by talent type (Employee, Trainee, Candidate, Presale) with their current status and age.',
  },
  {
    type: 'talent_anniversary_tomorrow',
    label: 'Work anniversary tomorrow',
    time: '10:00 Kyiv',
    desc: 'Sent daily for each talent whose hiring anniversary is the next calendar day.',
  },
  {
    type: 'job_applications_daily_summary',
    label: 'Job applications summary',
    time: '17:00 Kyiv',
    desc: 'A daily digest of all new job applications received during the day.',
  },
  {
    type: 'job_applications_weekly_summary',
    label: 'Weekly job applications summary',
    time: '09:00 Kyiv, every Monday',
    desc: 'A weekly digest of job applications for the past week: breakdown by manager, status distribution and conversion stats.',
  },
]

const credentialSteps = [
  {
    n: '1',
    icon: Settings,
    title: 'Create a Discord Bot',
    body: (
      <>
        Go to the{' '}
        <span className="wiki-mono text-xs bg-light-bg-tertiary dark:bg-dark-bg-tertiary px-1.5 py-0.5 rounded border border-light-border-primary dark:border-dark-border-primary">
          Discord Developer Portal
        </span>{' '}
        (discord.com/developers) → New Application → Bot tab → Add Bot.
      </>
    ),
  },
  {
    n: '2',
    icon: Key,
    title: 'Copy the Bot Token',
    body: 'In the Bot tab click "Reset Token" and copy the generated token. Keep it secret — it grants full bot access.',
  },
  {
    n: '3',
    icon: CheckCircle2,
    title: 'Invite the bot to your server',
    body: 'OAuth2 → URL Generator → select "bot" scope → tick "Send Messages" → open the generated URL and add the bot to your Discord server.',
  },
  {
    n: '4',
    icon: Hash,
    title: 'Get the Channel ID',
    body: 'In Discord: User Settings → Advanced → enable Developer Mode. Then right-click the target channel → "Copy Channel ID".',
  },
  {
    n: '5',
    icon: Settings,
    title: 'Create a credential set in account-service',
    body: (
      <>
        In account-service, create a new credential set with two fields:{' '}
        <span className="wiki-mono text-xs bg-light-bg-tertiary dark:bg-dark-bg-tertiary px-1 py-0.5 rounded border border-light-border-primary dark:border-dark-border-primary">bot_token</span>{' '}
        and{' '}
        <span className="wiki-mono text-xs bg-light-bg-tertiary dark:bg-dark-bg-tertiary px-1 py-0.5 rounded border border-light-border-primary dark:border-dark-border-primary">channel_id</span>.
        This credential set will be selected when configuring notifications.
      </>
    ),
  },
]

const faqItems = [
  {
    id: 'discord-q1',
    q: 'Notifications are not arriving — what should I check?',
    a: (
      <ol className="space-y-2 text-sm text-light-text-secondary dark:text-dark-text-secondary list-decimal list-inside">
        <li>
          <span className="font-medium text-light-text-primary dark:text-dark-text-primary">Environment variable</span>{' '}
          — confirm <span className="wiki-mono text-xs bg-light-bg-tertiary dark:bg-dark-bg-tertiary px-1 py-0.5 rounded border border-light-border-primary dark:border-dark-border-primary">DISCORD_NOTIFICATIONS_ENABLED=true</span> is set in the server's <span className="wiki-mono text-xs">.env</span>.
        </li>
        <li>
          <span className="font-medium text-light-text-primary dark:text-dark-text-primary">Active config</span>{' '}
          — open Notification Settings and confirm a config exists for the event type with <span className="italic">Is Active</span> toggled on.
        </li>
        <li>
          <span className="font-medium text-light-text-primary dark:text-dark-text-primary">Valid bot token</span>{' '}
          — the token in account-service must be current (tokens are invalidated when you reset them in Developer Portal).
        </li>
        <li>
          <span className="font-medium text-light-text-primary dark:text-dark-text-primary">Bot permissions</span>{' '}
          — the bot must be in the Discord server and have "Send Messages" permission in the target channel.
        </li>
      </ol>
    ),
  },
  {
    id: 'discord-q2',
    q: 'Can one event type send to multiple channels?',
    a: (
      <p className="text-sm text-light-text-secondary dark:text-dark-text-secondary">
        Yes. Create multiple notification configs for the same event type, each with a different credential set (different <span className="wiki-mono text-xs bg-light-bg-tertiary dark:bg-dark-bg-tertiary px-1 py-0.5 rounded border border-light-border-primary dark:border-dark-border-primary">channel_id</span>). Each active config will receive the message independently.
      </p>
    ),
  },
  {
    id: 'discord-q3',
    q: 'How do I change the scheduled notification time?',
    a: (
      <p className="text-sm text-light-text-secondary dark:text-dark-text-secondary">
        The cron schedules (10:00 and 17:00 Kyiv) are defined in{' '}
        <span className="wiki-mono text-xs bg-light-bg-tertiary dark:bg-dark-bg-tertiary px-1 py-0.5 rounded border border-light-border-primary dark:border-dark-border-primary">src/services/DiscordNotificationService.js</span>.
        Changing them requires a code edit and server restart. The timezone is fixed to <span className="italic">Europe/Kyiv</span>.
      </p>
    ),
  },
  {
    id: 'discord-q4',
    q: 'What do the Discord messages look like?',
    a: (
      <p className="text-sm text-light-text-secondary dark:text-dark-text-secondary">
        Messages are sent as <span className="font-medium text-light-text-primary dark:text-dark-text-primary">Discord Embeds</span> — structured cards with a colored left bar, a bold title, labeled fields, and a timestamp in the footer. Each event type uses a distinct accent color for quick visual identification.
      </p>
    ),
  },
]

const DiscordNotificationsGuide = () => {
  const [openFaq, setOpenFaq] = useState(null)

  return (
    <div className="space-y-10">

      {/* Overview */}
      <div id="discord-overview" className="scroll-mt-6 pl-4 border-l-2 border-primary-500/40 bg-primary-50/50 dark:bg-primary-900/10 rounded-r-lg py-3 pr-4">
        <h3 className="wiki-heading text-base font-semibold text-light-text-primary dark:text-dark-text-primary mb-1">
          What are Discord Notifications?
        </h3>
        <p className="text-sm text-light-text-secondary dark:text-dark-text-secondary mb-3">
          The system automatically sends messages to Discord channels when specific events occur.
          There are two delivery modes:
        </p>
        <div className="flex flex-wrap gap-2">
          {[
            { label: 'Real-time', icon: Zap, color: 'text-yellow-600 dark:text-yellow-400', bg: 'bg-yellow-50 dark:bg-yellow-900/20 border-yellow-200 dark:border-yellow-800' },
            { label: 'Scheduled (cron)', icon: Clock, color: 'text-blue-600 dark:text-blue-400', bg: 'bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800' },
          ].map(({ label, icon: Icon, color, bg }) => (
            <span key={label} className={`inline-flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-full border ${bg}`}>
              <Icon className={`w-3 h-3 ${color}`} />
              <span className={`wiki-mono font-medium ${color}`}>{label}</span>
            </span>
          ))}
        </div>
        <p className="mt-3 text-sm text-light-text-secondary dark:text-dark-text-secondary">
          All notifications are configured through the Admin UI — no code changes needed.
        </p>
      </div>

      {/* Flow diagram */}
      <div>
        <SubSectionHeading id="discord-how-it-works">How it works</SubSectionHeading>
        <div className="flex flex-col sm:flex-row items-center gap-2">
          {flowSteps.map((step, i) => {
            const Icon = step.icon
            return (
              <div key={step.label} className="flex sm:flex-col items-center gap-2 flex-1 w-full">
                <div className="flex-1 w-full p-4 rounded-xl border border-light-border-primary dark:border-dark-border-primary bg-light-bg-primary dark:bg-dark-bg-primary flex sm:flex-col items-center gap-3 sm:gap-2 sm:text-center">
                  <div className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 ${step.bg}`}>
                    <Icon className={`w-4 h-4 ${step.color}`} />
                  </div>
                  <div>
                    <p className="wiki-heading text-sm font-semibold text-light-text-primary dark:text-dark-text-primary">{step.label}</p>
                    <p className="text-xs text-light-text-secondary dark:text-dark-text-secondary mt-0.5">{step.desc}</p>
                  </div>
                </div>
                {i < flowSteps.length - 1 && (
                  <ArrowRight className="w-4 h-4 text-light-text-tertiary dark:text-dark-text-tertiary flex-shrink-0 sm:hidden" />
                )}
              </div>
            )
          })}
        </div>
        {/* Arrows between cards on desktop — rendered as a separate row */}
        <div className="hidden sm:grid mt-1" style={{ gridTemplateColumns: `repeat(${flowSteps.length}, 1fr)` }}>
          {flowSteps.map((_, i) => (
            <div key={i} className="flex justify-end items-center pr-1">
              {i < flowSteps.length - 1 && (
                <ArrowRight className="w-3.5 h-3.5 text-light-text-tertiary dark:text-dark-text-tertiary" />
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Real-time events */}
      <div>
        <SubSectionHeading id="discord-events-realtime">Real-time events</SubSectionHeading>
        <div className="flex items-center gap-1.5 mb-4">
          <span className="inline-flex items-center gap-1 wiki-mono text-[11px] px-2 py-0.5 rounded-full bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 text-yellow-700 dark:text-yellow-400">
            <Zap className="w-3 h-3" />
            Triggered instantly when the action occurs
          </span>
        </div>
        <div className="space-y-4">
          {realtimeEvents.map(ev => (
            <div
              key={ev.type}
              className="p-5 rounded-xl border border-light-border-primary dark:border-dark-border-primary border-l-4 border-l-yellow-500 bg-light-bg-primary dark:bg-dark-bg-primary"
            >
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 rounded-lg bg-yellow-50 dark:bg-yellow-900/20 flex items-center justify-center flex-shrink-0">
                  <Zap className="w-4 h-4 text-yellow-600 dark:text-yellow-400" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h4 className="wiki-heading text-sm font-semibold text-light-text-primary dark:text-dark-text-primary">
                      {ev.label}
                    </h4>
                    <span className="wiki-mono text-[11px] px-1.5 py-0.5 rounded bg-light-bg-tertiary dark:bg-dark-bg-tertiary border border-light-border-primary dark:border-dark-border-primary text-light-text-tertiary dark:text-dark-text-tertiary">
                      {ev.type}
                    </span>
                  </div>
                  <p className="text-sm text-light-text-secondary dark:text-dark-text-secondary mt-1">{ev.desc}</p>
                  <div className="mt-3">
                    <p className="wiki-mono text-[10px] uppercase tracking-wider text-light-text-tertiary dark:text-dark-text-tertiary mb-1.5">
                      Message includes:
                    </p>
                    <ul className="space-y-0.5">
                      {ev.embed.map(f => (
                        <li key={f} className="text-xs text-light-text-secondary dark:text-dark-text-secondary flex items-center gap-1.5">
                          <span className="w-1 h-1 rounded-full bg-light-text-tertiary dark:bg-dark-text-tertiary flex-shrink-0" />
                          {f}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Scheduled events */}
      <div>
        <SubSectionHeading id="discord-events-scheduled">Scheduled events</SubSectionHeading>
        <div className="flex items-center gap-1.5 mb-4">
          <span className="inline-flex items-center gap-1 wiki-mono text-[11px] px-2 py-0.5 rounded-full bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 text-blue-700 dark:text-blue-400">
            <Clock className="w-3 h-3" />
            Sent by cron job at a fixed time each day (Europe/Kyiv timezone)
          </span>
        </div>
        <div className="space-y-4">
          {scheduledEvents.map(ev => (
            <div
              key={ev.type}
              className="p-5 rounded-xl border border-light-border-primary dark:border-dark-border-primary border-l-4 border-l-blue-500 bg-light-bg-primary dark:bg-dark-bg-primary"
            >
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 rounded-lg bg-blue-50 dark:bg-blue-900/20 flex items-center justify-center flex-shrink-0">
                  <Clock className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h4 className="wiki-heading text-sm font-semibold text-light-text-primary dark:text-dark-text-primary">
                      {ev.label}
                    </h4>
                    <span className="wiki-mono text-[11px] px-1.5 py-0.5 rounded bg-light-bg-tertiary dark:bg-dark-bg-tertiary border border-light-border-primary dark:border-dark-border-primary text-light-text-tertiary dark:text-dark-text-tertiary">
                      {ev.type}
                    </span>
                    <span className="inline-flex items-center gap-1 wiki-mono text-[11px] px-1.5 py-0.5 rounded bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 text-blue-700 dark:text-blue-400">
                      <Clock className="w-2.5 h-2.5" />
                      {ev.time}
                    </span>
                  </div>
                  <p className="text-sm text-light-text-secondary dark:text-dark-text-secondary mt-1">{ev.desc}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Credential setup */}
      <div>
        <SubSectionHeading id="discord-credentials">Credential setup</SubSectionHeading>
        <p className="text-sm text-light-text-secondary dark:text-dark-text-secondary mb-6">
          Before creating a notification config you need a Discord bot with the correct permissions and a credential set stored in account-service.
        </p>
        <div className="space-y-6">
          {credentialSteps.map((step) => {
            const Icon = step.icon
            return (
              <div key={step.n} className="relative pl-14">
                <span className="wiki-mono absolute left-0 top-0 text-5xl font-bold text-light-border-primary dark:text-dark-bg-tertiary select-none leading-none">
                  {step.n}
                </span>
                <div className="absolute left-10 top-1 bottom-1 w-0.5 bg-primary-500 rounded-full" />
                <div className="pt-0.5">
                  <div className="flex items-center gap-2 mb-1">
                    <Icon className="w-4 h-4 text-primary-500 flex-shrink-0" />
                    <h5 className="wiki-heading text-sm font-semibold text-light-text-primary dark:text-dark-text-primary">
                      {step.title}
                    </h5>
                  </div>
                  <p className="text-sm text-light-text-secondary dark:text-dark-text-secondary">
                    {step.body}
                  </p>
                </div>
              </div>
            )
          })}
        </div>
      </div>

      {/* Admin UI */}
      <div>
        <SubSectionHeading id="discord-admin-ui">Admin UI — Notification Settings</SubSectionHeading>
        <div className="pl-4 border-l-2 border-primary-500/40 bg-primary-50/50 dark:bg-primary-900/10 rounded-r-lg py-3 pr-4 mb-6">
          <p className="text-sm text-light-text-secondary dark:text-dark-text-secondary">
            Go to <span className="font-medium text-light-text-primary dark:text-dark-text-primary">Admin → Notification Settings</span> (route <span className="wiki-mono text-xs bg-light-bg-tertiary dark:bg-dark-bg-tertiary px-1 py-0.5 rounded border border-light-border-primary dark:border-dark-border-primary">/notification-settings</span>).
            This page lists all configured notification rules and lets you create, edit, or deactivate them.
          </p>
        </div>
        <p className="text-sm font-medium text-light-text-primary dark:text-dark-text-primary mb-3">To add a new notification config:</p>
        <div className="space-y-3">
          {[
            { n: '1', text: 'Click "Add Configuration"' },
            { n: '2', text: 'Choose the Event Type from the dropdown (e.g. talent_status_changed)' },
            { n: '3', text: 'Select the Account — this determines which credential sets are available' },
            { n: '4', text: 'Select the Credential Set (the bot_token + channel_id pair created in account-service)' },
            { n: '5', text: 'Make sure Is Active is enabled, then save' },
          ].map(item => (
            <div key={item.n} className="flex items-start gap-3">
              <span className="wiki-mono flex-shrink-0 w-5 h-5 rounded-full bg-light-bg-tertiary dark:bg-dark-bg-tertiary border border-light-border-primary dark:border-dark-border-primary text-[11px] flex items-center justify-center text-light-text-secondary dark:text-dark-text-secondary font-semibold">
                {item.n}
              </span>
              <p className="text-sm text-light-text-secondary dark:text-dark-text-secondary pt-0.5">{item.text}</p>
            </div>
          ))}
        </div>
        <div className="mt-5 flex gap-3 p-4 rounded-lg border border-light-border-secondary dark:border-dark-border-secondary bg-light-bg-secondary dark:bg-dark-bg-secondary">
          <AlertCircle className="w-4 h-4 text-primary-500 flex-shrink-0 mt-0.5" />
          <p className="text-sm text-light-text-secondary dark:text-dark-text-secondary">
            <span className="font-medium text-light-text-primary dark:text-dark-text-primary">Multiple channels per event:</span>{' '}
            Create multiple configs for the same event type, each with a different credential set. The message will be delivered to every active channel independently.
          </p>
        </div>
      </div>

      {/* Environment variable */}
      <div>
        <SubSectionHeading id="discord-env">Master enable switch</SubSectionHeading>
        <p className="text-sm text-light-text-secondary dark:text-dark-text-secondary mb-4">
          All Discord notifications are gated behind a single environment variable. If it is absent or set to anything other than <span className="wiki-mono text-xs bg-light-bg-tertiary dark:bg-dark-bg-tertiary px-1 py-0.5 rounded border border-light-border-primary dark:border-dark-border-primary">true</span>, no messages will be sent — regardless of what configs exist in the database.
        </p>
        <div className="rounded-xl border border-light-border-primary dark:border-dark-border-primary overflow-hidden">
          <div className="px-3 py-2 border-b border-light-border-primary dark:border-dark-border-primary bg-light-bg-secondary dark:bg-dark-bg-secondary flex items-center gap-2">
            <span className="wiki-mono text-[11px] text-light-text-tertiary dark:text-dark-text-tertiary">.env</span>
          </div>
          <div className="p-4 bg-light-bg-primary dark:bg-dark-bg-primary">
            <code className="wiki-mono text-sm text-light-text-primary dark:text-dark-text-primary">
              DISCORD_NOTIFICATIONS_ENABLED=<span className="text-primary-500">true</span>
            </code>
          </div>
        </div>
      </div>

      {/* FAQ */}
      <div>
        <SubSectionHeading id="discord-faq">Common Questions</SubSectionHeading>
        <div className="divide-y divide-light-border-primary dark:divide-dark-border-primary">
          {faqItems.map(({ id, q, a }) => {
            const isOpen = openFaq === id
            return (
              <div key={id} id={id} className="scroll-mt-6">
                <div className="flex items-center justify-between py-4 gap-3">
                  <button
                    onClick={() => setOpenFaq(isOpen ? null : id)}
                    className="text-sm font-medium text-left text-light-text-primary dark:text-dark-text-primary hover:text-primary-500 transition-colors flex-1 min-w-0"
                  >
                    {q}
                  </button>
                  <button onClick={() => setOpenFaq(isOpen ? null : id)} className="flex-shrink-0">
                    <ChevronDown
                      className={[
                        'w-4 h-4 text-light-text-tertiary dark:text-dark-text-tertiary transition-transform duration-200',
                        isOpen ? 'rotate-180' : ''
                      ].join(' ')}
                    />
                  </button>
                </div>
                {isOpen && (
                  <div className="pb-4 pr-6">
                    {a}
                  </div>
                )}
              </div>
            )
          })}
        </div>
      </div>

    </div>
  )
}

export default DiscordNotificationsGuide
